import { useState, useEffect } from "react";
import { useGeolocation } from "../../../hooks/useGeolocation";
import { calcDistance, formatDistance } from "../../../utils/distance";
import { venueCoords } from "../../../mocks/venueCoords";
import { allVenues } from "../../../mocks/allVenues";
import { useNavigate } from "react-router-dom";

const CATEGORY_ICONS: Record<string, string> = {
  "Tarihi Yerler": "ri-ancient-pavilion-line",
  "Lezzet Durakları": "ri-restaurant-line",
  "Konaklama": "ri-hotel-line",
  "Doğa ve Rotalar": "ri-plant-line",
  "Eğlence": "ri-gamepad-line",
  "Gezilecek Yerler": "ri-eye-line",
};

interface NearbyVenue {
  id: string;
  name: string;
  category: string;
  image: string;
  district: string;
  rating: number;
  distanceM: number;
  isOpen: boolean;
}

export default function NearbySection() {
  const navigate = useNavigate();
  const geo = useGeolocation();
  const [nearby, setNearby] = useState<NearbyVenue[]>([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (geo.status !== "success" || geo.lat === null || geo.lng === null) return;
    const withDist: NearbyVenue[] = allVenues
      .map((v) => {
        const coord = venueCoords.find((c) => c.id === v.id);
        if (!coord) return null;
        const distM = calcDistance(geo.lat!, geo.lng!, coord.lat, coord.lng);
        return {
          id: v.id,
          name: v.name,
          category: v.category,
          image: v.image,
          district: v.district,
          rating: v.rating,
          distanceM: distM,
          isOpen: v.isOpen,
        } as NearbyVenue;
      })
      .filter(Boolean)
      .sort((a, b) => a!.distanceM - b!.distanceM)
      .slice(0, 12) as NearbyVenue[];
    setNearby(withDist);
  }, [geo.status, geo.lat, geo.lng]);

  const categories = ["all", ...Array.from(new Set(nearby.map((v) => v.category)))];
  const filtered = filter === "all" ? nearby : nearby.filter((v) => v.category === filter);
  const shown = filtered.slice(0, 8);

  return (
    <section className="py-20 md:py-28" style={{ background: "#f7f5f2" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-4">
            Konum Tabanlı
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Yakınımdaki Mekanlar
          </h2>
          <p className="text-stone-500 text-base max-w-2xl mx-auto leading-relaxed">
            Bulunduğunuz konuma göre en yakın mekanları keşfedin. Mesafe sıralamalı sonuçlar anlık güncellenir.
          </p>
        </div>

        {geo.status === "idle" && (
          <div className="max-w-lg mx-auto text-center bg-white border border-stone-200 rounded-2xl p-10 mb-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-2-line text-2xl text-emerald-600" />
            </div>
            <h3 className="font-bold text-stone-900 text-xl mb-2">Konumunuzu Paylaşın</h3>
            <p className="text-stone-500 text-sm mb-6 leading-relaxed">
              Size en yakın mekanları mesafeye göre sıralayıp gösterebilmek için konum izni almanız gerekiyor.
            </p>
            <button
              onClick={geo.requestLocation}
              className="px-8 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-crosshair-2-line" />
              Konumumu Kullan
            </button>
          </div>
        )}

        {geo.status === "loading" && (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-stone-500 text-sm">Konumunuz alınıyor...</p>
          </div>
        )}

        {geo.status === "error" && (
          <div className="max-w-lg mx-auto text-center bg-red-50 border border-red-100 rounded-2xl p-8 mb-8">
            <i className="ri-error-warning-line text-3xl text-red-400 mb-3" />
            <p className="text-red-700 font-semibold mb-2">Konum Alınamadı</p>
            <p className="text-red-600 text-sm mb-4">{geo.error}</p>
            <button
              onClick={geo.requestLocation}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Tekrar Dene
            </button>
          </div>
        )}

        {geo.status === "success" && nearby.length > 0 && (
          <>
            {/* Category filter */}
            <div className="flex flex-wrap items-center gap-2 mb-6 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    filter === cat
                      ? "bg-stone-900 text-white"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
                  }`}
                >
                  {cat === "all" ? "Tümü" : cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {shown.map((v) => (
                <div
                  key={v.id}
                  onClick={() => navigate(`/venue/${v.id}`)}
                  className="bg-white rounded-2xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform border border-stone-100"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={v.image}
                      alt={v.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 px-2.5 py-1 bg-stone-900/80 text-white text-xs font-bold rounded-full backdrop-blur-sm flex items-center gap-1">
                      <i className="ri-walk-line text-xs" />
                      {formatDistance(v.distanceM)}
                    </div>
                    <span
                      className={`absolute top-3 left-3 w-2 h-2 rounded-full ${v.isOpen ? "bg-emerald-400" : "bg-red-400"}`}
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${CATEGORY_ICONS[v.category] ?? "ri-map-pin-line"} text-emerald-600 text-sm`} />
                      </div>
                      <span className="text-xs font-semibold text-emerald-700">{v.category}</span>
                    </div>
                    <h3 className="font-bold text-stone-900 text-sm mb-1 leading-tight">{v.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-400">{v.district}</span>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs" />
                        <span className="text-xs font-bold text-stone-700">{v.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length > 8 && (
              <div className="text-center mt-10">
                <button
                  onClick={() => navigate("/map")}
                  className="px-8 py-3 border border-stone-300 rounded-xl text-stone-700 font-semibold text-sm hover:bg-white transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  Haritada Tümünü Gör
                  <i className="ri-map-2-line" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
